import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Paper, InputAdornment,
  Alert, CircularProgress,
} from '@mui/material';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import API_BASE_URL from '../../Config/config';

const TSGLogo = ({ size = 44 }) => {
  const [imgError, setImgError] = useState(false);
  if (imgError) {
    return (
      <Box sx={{
        width: size, height: size, borderRadius: '12px',
        background: 'linear-gradient(135deg,#6C3BFF,#8B5CF6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto',
      }}>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: size * 0.46 }}>T</Typography>
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src="https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png"
      alt="The Skill Genie"
      loading="lazy"
      onError={() => setImgError(true)}
      sx={{ height: size, width: 'auto', objectFit: 'contain', display: 'block', mx: 'auto' }}
    />
  );
};

const NextGenForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setError('Please enter your email address');
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/nextgen-builder/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: '#F8FAFC',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      px: { xs: 2, sm: 3 }, py: 5,
      fontFamily: '"Plus Jakarta Sans","Inter",system-ui,sans-serif',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Subtle background gradient blobs */}
      <Box sx={{ position:'absolute',top:-160,right:-160,width:480,height:480,borderRadius:'50%',
                  background:'radial-gradient(circle,rgba(108,59,255,0.06) 0%,transparent 70%)',
                  pointerEvents:'none' }} />
      <Box sx={{ position:'absolute',bottom:-120,left:-120,width:380,height:380,borderRadius:'50%',
                  background:'radial-gradient(circle,rgba(139,92,246,0.05) 0%,transparent 70%)',
                  pointerEvents:'none' }} />

      <motion.div
        initial={{ opacity:0, y:18 }}
        animate={{ opacity:1, y:0 }}
        transition={{ duration:0.35, ease:[0.25,0.1,0.25,1] }}
        style={{ width:'100%', maxWidth:420, position:'relative', zIndex:1 }}
      >
        {/* Back link */}
        <Box sx={{ mb:3 }}>
          <Link to="/nextgen-builder/login" style={{ textDecoration:'none', display:'inline-flex',
              alignItems:'center', gap:6, color:'#64748B', fontWeight:600, fontSize:'0.85rem' }}>
            <ArrowBackRoundedIcon sx={{ fontSize:16 }} />
            Back to Sign In
          </Link>
        </Box>

        <Paper elevation={0} sx={{
          p:{ xs:3.5, sm:5 },
          borderRadius:'20px',
          border:'1px solid #E5E7EB',
          bgcolor:'#FFFFFF',
          boxShadow:'0 1px 3px rgba(0,0,0,0.06),0 4px 8px rgba(0,0,0,0.04),0 20px 48px -12px rgba(108,59,255,0.1)',
        }}>
          {success ? (
            /* ── Success state ────────────────────────────── */
            <Box sx={{ textAlign:'center', py:1 }}>
              <Box sx={{
                width:64, height:64, borderRadius:'18px', mx:'auto', mb:3,
                bgcolor:'rgba(22,163,74,0.08)', border:'1px solid rgba(22,163,74,0.18)',
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
                <MarkEmailReadRoundedIcon sx={{ color:'#16A34A', fontSize:32 }} />
              </Box>
              <Typography variant="h5" sx={{ color:'#0F172A', fontWeight:800, mb:1 }}>
                Check your inbox
              </Typography>
              <Typography variant="body2" sx={{ color:'#64748B', mb:1, lineHeight:1.75 }}>
                If an account exists for{' '}
                <Box component="span" sx={{ color:'#0F172A', fontWeight:600 }}>{email}</Box>,
                you'll receive a password reset link shortly.
              </Typography>
              <Typography variant="caption" sx={{ color:'#94A3B8', display:'block', mb:3.5 }}>
                Don't see it? Check your spam folder.
              </Typography>
              <Button
                component={Link} to="/nextgen-builder/login"
                variant="outlined" fullWidth
                sx={{
                  py:1.5, borderRadius:'12px', fontWeight:700,
                  borderColor:'#E2E8F0', color:'#0F172A',
                  '&:hover':{ borderColor:'#6C3BFF', bgcolor:'rgba(108,59,255,0.04)' },
                }}
              >
                Return to Sign In
              </Button>
            </Box>
          ) : (
            /* ── Request form ─────────────────────────────── */
            <>
              <Box sx={{ textAlign:'center', mb:3.5 }}>
                <TSGLogo size={44} />
                <Typography variant="h5" sx={{ color:'#0F172A', fontWeight:800, mt:2.5, mb:0.75 }}>
                  Forgot your password?
                </Typography>
                <Typography variant="body2" sx={{ color:'#64748B', maxWidth:320, mx:'auto' }}>
                  Enter the email address associated with your account and we'll send you a reset link.
                </Typography>
              </Box>

              {error && <Alert severity="error" sx={{ mb:2.5 }}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth label="Email Address" name="email" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailRoundedIcon sx={{ color:'#94A3B8', fontSize:19 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb:3 }}
                />

                <Button
                  type="submit" fullWidth size="large" disabled={loading}
                  sx={{
                    py:1.65, borderRadius:'12px', fontWeight:700, fontSize:'0.95rem',
                    letterSpacing:'-0.01em', color:'#fff',
                    background:'linear-gradient(135deg, #6C3BFF 0%, #8B5CF6 100%)',
                    boxShadow:'0 4px 14px rgba(108,59,255,0.32)',
                    '&:hover': {
                      background:'linear-gradient(135deg, #5020E0 0%, #7C3AED 100%)',
                      boxShadow:'0 8px 28px rgba(108,59,255,0.44)',
                      transform:'translateY(-1px)',
                    },
                    '&:disabled':{ opacity:0.6, boxShadow:'none', transform:'none' },
                  }}
                >
                  {loading ? <CircularProgress size={20} sx={{ color:'#fff' }} /> : 'Send Reset Link'}
                </Button>
              </Box>

              <Box sx={{ textAlign:'center', mt:3 }}>
                <Typography variant="body2" sx={{ color:'#64748B' }}>
                  Remember your password?{' '}
                  <Link to="/nextgen-builder/login"
                    style={{ color:'#6C3BFF', fontWeight:700, textDecoration:'none' }}>
                    Sign In
                  </Link>
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </motion.div>
    </Box>
  );
};

export default NextGenForgotPassword;
